import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { Redirect, useLocation } from "wouter";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Tag } from "lucide-react";
import { useCategories } from "@/hooks/use-categories";
import { ImageUpload } from "@/components/ImageUpload";

const TOWNS = ["Ayr","Kilmarnock","Irvine","Prestwick","Troon","Saltcoats","Ardrossan","Largs","Kilwinning","Stewarton","Maybole","Girvan","Cumnock","Auchinleck","Newmilns","Kilbirnie","Beith","Dalry"];

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

const EMPTY = {
  businessName: "", title: "", description: "", category: "", town: "Ayr",
  imageUrl: "", startDate: "", endDate: "", redemptionInstructions: "",
};

export default function AdminCreateDeal() {
  const { user, isLoading: authLoading } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const dealCategories = useCategories("deal");
  useEffect(() => {
    if (!form.category && dealCategories.length > 0) setForm(f => ({ ...f, category: dealCategories[0]! }));
  }, [dealCategories]);

  if (authLoading) return null;
  if (user?.role !== "admin") return <Redirect to="/" />;

  const set = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }));
  const f = (k: keyof typeof EMPTY) => form[k];

  const handleSubmit = async () => {
    if (!form.businessName || !form.title || !form.description) {
      toast({ title: "Please fill in all required fields", variant: "destructive" }); return;
    }
    setSaving(true);
    try {
      const r = await fetch("/api/admin/deals", {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({
          businessName: form.businessName, title: form.title, description: form.description,
          category: form.category, town: form.town,
          imageUrl: form.imageUrl || null,
          startDate: form.startDate || null, endDate: form.endDate || null,
          redemptionInstructions: form.redemptionInstructions || null,
        }),
      });
      if (!r.ok) { const d = await r.json() as { error?: string }; throw new Error(d.error ?? "Failed"); }
      toast({ title: "Deal created and approved" });
      setForm(EMPTY);
      setLocation("/admin/pending");
    } catch (e) {
      toast({ title: e instanceof Error ? e.message : "Failed to create deal", variant: "destructive" });
    } finally { setSaving(false); }
  };

  return (
    <AdminLayout current="/admin/deals/create">
      <div className="space-y-6 max-w-2xl">
        <div className="flex items-center gap-3">
          <Tag size={22} className="text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-primary">Add Deal</h1>
            <p className="text-sm text-slate-500">Created deals are automatically approved and published.</p>
          </div>
        </div>

        <Card>
          <CardContent className="pt-5 space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="deal-business-name" className="block text-xs font-semibold text-slate-500 mb-1">Business Name *</label>
                <Input id="deal-business-name" value={f("businessName")} onChange={e => set("businessName", e.target.value)} placeholder="e.g. The Corner Café" />
              </div>

              <div>
                <label htmlFor="deal-title" className="block text-xs font-semibold text-slate-500 mb-1">Deal Title *</label>
                <Input id="deal-title" value={f("title")} onChange={e => set("title", e.target.value)} placeholder="e.g. 20% Off All Meals" />
              </div>

              <div>
                <label htmlFor="deal-category" className="block text-xs font-semibold text-slate-500 mb-1">Category *</label>
                <select id="deal-category" value={f("category")} onChange={e => set("category", e.target.value)} className="w-full h-10 text-sm border border-input rounded-md px-3 bg-background">
                  {dealCategories.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>

              <div>
                <label htmlFor="deal-town" className="block text-xs font-semibold text-slate-500 mb-1">Town *</label>
                <select id="deal-town" value={f("town")} onChange={e => set("town", e.target.value)} className="w-full h-10 text-sm border border-input rounded-md px-3 bg-background">
                  {TOWNS.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>

              <div>
                <label htmlFor="deal-start-date" className="block text-xs font-semibold text-slate-500 mb-1">Start Date</label>
                <Input id="deal-start-date" type="date" value={f("startDate")} onChange={e => set("startDate", e.target.value)} />
              </div>

              <div>
                <label htmlFor="deal-end-date" className="block text-xs font-semibold text-slate-500 mb-1">End Date</label>
                <Input id="deal-end-date" type="date" value={f("endDate")} onChange={e => set("endDate", e.target.value)} />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-slate-500 mb-1">Image</label>
                <ImageUpload
                  value={f("imageUrl")}
                  onChange={v => set("imageUrl", v)}
                  label="Upload Image"
                  stockCategory="deal"
                />
              </div>

              <div className="sm:col-span-2">
                <label htmlFor="deal-description" className="block text-xs font-semibold text-slate-500 mb-1">Description *</label>
                <Textarea id="deal-description" value={f("description")} onChange={e => set("description", e.target.value)} rows={3} placeholder="Describe the deal in detail…" />
              </div>

              <div className="sm:col-span-2">
                <label htmlFor="deal-redemption-instructions" className="block text-xs font-semibold text-slate-500 mb-1">Redemption Instructions</label>
                <Textarea id="deal-redemption-instructions" value={f("redemptionInstructions")} onChange={e => set("redemptionInstructions", e.target.value)} rows={2} placeholder="How customers can claim this deal…" />
              </div>
            </div>

            <div className="flex gap-3 pt-2 border-t border-slate-100">
              <Button
                disabled={saving || !form.businessName || !form.title || !form.description}
                onClick={handleSubmit}
                className="bg-primary hover:bg-primary/90"
              >
                {saving ? "Creating…" : "Create Deal"}
              </Button>
              <Button variant="outline" onClick={() => setLocation("/admin/pending")}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
